import { useState } from "react";
import { useNotification } from "./useNotification";
import { CategoryService } from "@root/services/category";
import { PAGINATION_DEFAULTS, TOAST_VARIANT } from "@root/constants";

export const useCategory = () => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);
  const [updateCategoryLoading, setUpdateCategoryLoading] = useState(false);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);
  const [getCategoriesLoading, setGetCategoriesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [page, setPage] = useState(1);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [pageCount, setPageCount] = useState(0);

  const categoryService = new CategoryService();
  const { showNotification } = useNotification();

  const payload = {
    limit: PAGINATION_DEFAULTS.PAGESIZE,
    offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    search: search,
  };

  const fetchCategories = async (payload: any) => {
    setGetCategoriesLoading(true);
    try {
      const res = await categoryService.fetchCategories(payload);
      setCategoriesData(res?.data?.data);
      setPageCount(res?.data?.data?.count);
    } catch (error: unknown) {
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: "Failed to get categories",
      });
    } finally {
      setGetCategoriesLoading(false);
    }
  };

  const updateCategory = async (id: number, payload: any) => {
    setUpdateCategoryLoading(true);
    try {
      await categoryService.updateCategory(id, payload);
      showNotification({
        type: "success",
        message: "Category updated successfully",
      });
      setEditingId(null);
      setEditingValue("");
      fetchCategories(payload);
    } catch (err) {
      showNotification({
        type: "error",
        message: "Failed to update the category",
      });
    } finally {
      setUpdateCategoryLoading(false);
    }
  };

  const addCategory = async (payload: any) => {
    setAddCategoryLoading(true);
    try {
      await categoryService.addCategory(payload);
      showNotification({
        type: "success",
        message: "Category added successfully",
      });
      setEditingValue("");
      setAddNewCategory(false);
      setNewCategory("");
      fetchCategories(payload);
    } catch (err) {
      showNotification({
        type: "error",
        message: "Failed to add category",
      });
    } finally {
      setAddCategoryLoading(false);
    }
  };

  const deleteCategory = async (id: number) => {
    setDeleteCategoryLoading(true);
    try {
      await categoryService.deleteCategory(id);
      showNotification({
        type: "success",
        message: "Category deleted successfully",
      });
      fetchCategories(payload);
    } catch (err) {
      showNotification({
        type: "success",
        message: "Failed to delete category",
      });
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  return {
    addCategoryLoading,
    getCategoriesLoading,
    updateCategoryLoading,
    deleteCategoryLoading,
    addCategory,
    updateCategory,
    deleteCategory,
    fetchCategories,
    categoriesData,
    setCategoriesData,
    search,
    setSearch,
    newCategory,
    setNewCategory,
    page,
    setPage,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
    pageCount,
  };
};
